/**
 * Guard enforcing a company's Require MFA setting.
 *
 * A Ninety session outlives a Cognito one, so a long-lived user reaches this guard with no readable
 * MFA preference. That state used to be indistinguishable from a Microsoft SSO user — both make the
 * Amplify calls throw — and everyone in it was waved past the company setting.
 *
 * `authProvider` tells them apart. It is a claim on this session's own tokens, so it answers for the
 * browser asking; the person record holds only the most recent login on any device, and would let an
 * SSO sign-in elsewhere exempt a Cognito-native session here.
 *
 * Logout rather than `return false`: setup() needs the same Cognito session that is missing, and
 * redirecting to /login while the Ninety token is still valid bounces off IsAnonymousGuard to /home.
 *
 * @returns Observable<boolean> indicating whether navigation is allowed.
 */
import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { Action, Store } from '@ngrx/store';
import { Observable, catchError, combineLatest, defer, filter, map, of, switchMap, tap } from 'rxjs';

import { AuthStateSelectors } from '@ninety/platform/state/app-global/auth/auth.selectors';
import { LoginActions, LoginMfaActions } from '@ninety/platform/state/app-global/auth/login/login.actions';
import { LogOutActions } from '@ninety/platform/state/app-global/auth/logout/logout.actions';
import {
  AmplifyService,
  isMissingSession,
  retryCognitoRead,
} from '@ninety/platform/state/app-global/auth/services/amplify.service';
import { selectCompany } from '@ninety/platform/state/app-global/company/company-state.selectors';

/** Distinguishes "Cognito says no factors" from "Cognito could not be reached at all". */
const COGNITO_UNAVAILABLE = Symbol('cognito-unavailable');
/** Cognito answered that there is no session, which a plain logout handles. */
const SESSION_GONE = Symbol('session-gone');

type Unreadable = typeof COGNITO_UNAVAILABLE | typeof SESSION_GONE;
const isUnreadable = <T>(value: T | Unreadable): value is Unreadable =>
  value === COGNITO_UNAVAILABLE || value === SESSION_GONE;
const unreadable = (error: unknown): Unreadable => (isMissingSession(error) ? SESSION_GONE : COGNITO_UNAVAILABLE);

type MfaDecision = { allowed: boolean; action?: Action };

export const companyMfaSetupGuard: CanActivateFn = () => {
  const store = inject(Store);
  const amplifyService = inject(AmplifyService);

  return combineLatest([store.select(selectCompany), store.select(AuthStateSelectors.authProvider)]).pipe(
    // Company arrives late on a company switch. Waiting on it also settles authProvider, which the
    // reducer sets from the same action before the effect that dispatches the company.
    filter(([company]) => !!company),
    // Reading Cognito behind the setting, not alongside it: a company that does not enforce MFA
    // would otherwise wait out the retries for an answer nothing consults.
    switchMap(([company, authProvider]): Observable<MfaDecision> => {
      if (!company.settings.requireMfa) return of({ allowed: true });

      // defer: each call wraps a fresh promise, so a bare retry would resubscribe to one settled rejection.
      return combineLatest([
        defer(() => amplifyService.fetchMfaPreference()).pipe(
          retryCognitoRead(),
          catchError((error: unknown) => of(unreadable(error)))
        ),
        defer(() => amplifyService.getUserAttributes()).pipe(
          retryCognitoRead(),
          catchError((error: unknown) => of(unreadable(error)))
        ),
      ]).pipe(
        map(([mfaPreference, cognitoAttributes]): MfaDecision => {
          // Both calls are the same GetUser, so only both failing means the session is really gone --
          // and only then is there no live answer to prefer over the claim.
          if (isUnreadable(mfaPreference) && isUnreadable(cognitoAttributes)) {
            if (authProvider === 'Google' || authProvider === 'Microsoft') return { allowed: true };

            // Cognito-native, or a session predating the claim: unverifiable either way. Only an outage
            // gets the MFA message; an expired session is an ordinary logout.
            if (mfaPreference === SESSION_GONE || cognitoAttributes === SESSION_GONE) {
              return { allowed: false, action: LogOutActions.initiateLogOut() };
            }
            return { allowed: false, action: LoginActions.mfaPreferenceUnavailable() };
          }

          // identities is present only on a Cognito-federated (Google) login.
          const userIsSsoCognito = !isUnreadable(cognitoAttributes) && !!cognitoAttributes['identities'];
          if (userIsSsoCognito) return { allowed: true };

          if (!isUnreadable(mfaPreference) && mfaPreference.enabled?.length > 0) return { allowed: true };

          // An unreadable preference is not the same as no factors. Enrolling on it walks a user who
          // may already have an authenticator app through SMS setup, so send them back to log in.
          if (isUnreadable(mfaPreference)) {
            return { allowed: false, action: LoginActions.mfaPreferenceUnavailable() };
          }

          return { allowed: false, action: LoginMfaActions.setup() };
        })
      );
    }),
    tap(({ action }) => {
      if (action) store.dispatch(action);
    }),
    map(({ allowed }) => allowed)
  );
};
